import React, { FC, useEffect, useState } from 'react'

import {
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LifebuoyIcon,
  RectangleStackIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import Link from 'next/link'
import Switch from 'react-switch'

import EscalationApprovalDrawer from '../EscalationApprovalDrawer'
import { CustomerMessages } from './CustomerMessages'
import { Escalations } from './Escalations/index'
import { SubTabButton } from './SubTabButton'
import { TasksV2 } from './TasksV2'
import { UserProfile } from './UserProfile'
import escalations from '@/assets/img/escalations.svg'
import { setAlertsTypes, setDealAdvisorOpen, setDealAdvisorTab } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import UserAvatar from '@/components/Avatar/UserAvatar'
import { Alerts } from '@/features/SidePanel/AlertsV2'
import { FileContacts } from '@/features/SidePanel/FileContacts'
import { Notes } from '@/features/SidePanel/Notes'
import { Support } from '@/features/SidePanel/Support'
import { useCurrentApplication } from '@/graphql/services/application'
import { useAlertsTypesQuery, useCountAlertsQuery } from '@/services/alerts'
import { useCountEscalationsQuery } from '@/services/escalations'
import { useCountStreamingNotesQuery } from '@/services/streamingNote'
import { usePatchUserPreferencesMutation } from '@/services/uiPreferences'
import { useGetLoggedUserQuery, useSetUserControlledFeatureFlagMutation } from '@/services/user'

export interface ISidePanel {
  hideQueue?: boolean
  hideNotes?: boolean
  hideMessages?: boolean
  hideEscalations?: boolean
  hideNotifications?: boolean
  hideAlerts?: boolean
  hideUserProfile?: boolean
}

export const SidePanel: FC<ISidePanel> = ({
  hideQueue,
  hideNotes,
  hideMessages,
  hideEscalations,
  hideAlerts,
  hideUserProfile,
}) => {
  const dispatch = useAppDispatch()
  const { data: application } = useCurrentApplication()

  const [activeGroupObject, setActiveGroupObject] = useState(null)

  const { data: escalationsCount, refetch: escalationsCountRefetch } = useCountEscalationsQuery(
    application?.id,
    {
      skip: !application?.id,
    }
  )

  const { data: streamingNotesCount } = useCountStreamingNotesQuery(application?.id, {
    skip: !application?.id,
  })

  const {
    data: alertsCount,
    refetch: alertsCountRefetch,
    isUninitialized: isUninitializedAlertsCount,
  } = useCountAlertsQuery(application?.id, {
    skip: !application?.id,
  })

  const { data: alertsTypes } = useAlertsTypesQuery(application?.id, {
    skip: !application?.id,
  })
  dispatch(setAlertsTypes(alertsTypes))

  useEffect(() => {
    if (!isUninitializedAlertsCount) alertsCountRefetch()
  }, [alertsTypes])

  const subTabs: {
    name: string
    title: string
    shortTitle?: string
    component: JSX.Element
    icon: any
    visible: boolean
    count: number
  }[] = [
    {
      name: 'userProfile',
      title: 'User Profile',
      shortTitle: 'Profile',
      component: <UserProfile />,
      icon: UserAvatar,
      visible: !hideUserProfile,
      count: 0,
    },
    {
      name: 'queue',
      title: 'Tasks',
      component: <TasksV2 activeGroupChange={setActiveGroupObject} />,
      icon: ClipboardDocumentCheckIcon,
      visible: !hideQueue,
      count: 0,
    },
    {
      name: 'notes',
      title: 'Notes',
      component: <Notes activeGroupObject={activeGroupObject} />,
      icon: DocumentTextIcon,
      visible: !hideNotes,
      count: streamingNotesCount,
    },
    {
      name: 'messages',
      title: 'Customer Messages',
      shortTitle: 'Messages',
      component: <CustomerMessages />,
      icon: ChatBubbleLeftEllipsisIcon,
      visible: !hideMessages,
      count: 0,
    },
    {
      name: 'escalations',
      title: 'Escalations',
      component: <Escalations escalationsCountRefetch={escalationsCountRefetch} />,
      icon: escalations,
      visible: !hideEscalations,
      count: escalationsCount,
    },
    {
      name: 'alerts',
      title: 'Alerts',
      component: <Alerts />,
      icon: ExclamationTriangleIcon,
      visible: !hideAlerts,
      count: alertsCount ? alertsCount : 0,
    },
    {
      name: 'fileContacts',
      title: 'File Contacts',
      shortTitle: 'Contacts',
      component: <FileContacts application={application} />,
      icon: UsersIcon,
      visible: true,
      count: 0,
    },
    {
      name: 'support',
      title: 'Support',
      component: <Support />,
      icon: LifebuoyIcon,
      visible: true,
      count: 0,
    },
  ]

  const open = useAppSelector((state) => state.general.dealAdvisorOpen)
  const activeSubTab = useAppSelector((state) => state.general.dealAdvisorTab)

  const activeTabObject = subTabs.find((tab) => tab.name === activeSubTab)

  const { data: user } = useGetLoggedUserQuery()

  const [setUserControlledFF] = useSetUserControlledFeatureFlagMutation()

  const isUserControlledFF = user?.data?.is_user_controller_feature_flag_on

  const [patchUserPreferences] = usePatchUserPreferencesMutation()

  return (
    <div className='relative'>
      <EscalationApprovalDrawer />
      <div
        className='flex bg-white sticky top-0 h-[calc(100vh-28px)]'
        data-test='sidePanel-container'
      >
        <div className='w-auto p-1 3xl:p-2 border-l flex flex-col'>
          <div className='flex flex-col items-center gap-6 py-5 flex-grow'>
            {subTabs.map((tab, key) => {
              if (tab.visible) {
                return (
                  <>
                    <div
                      key={key}
                      className={`flex items-center justify-center
                    cursor-pointer w-[48px] h-[48px] rounded-full hover:bg-blue-200 hover:text-blue-800
                    ${activeSubTab === tab.name ? 'bg-blue-200' : ''}`}
                      onClick={() => {
                        dispatch(setDealAdvisorOpen(true))
                        dispatch(setDealAdvisorTab(tab.name))
                        patchUserPreferences({
                          id: user?.id,
                          ui_preferences: {
                            ...user?.data?.ui_preferences,
                            dealAdvisorOpen: true,
                            dealAdvisorTab: tab.name,
                          },
                        })
                      }}
                      data-test={`side-panel-navitem-${tab.name}`}
                    >
                      <SubTabButton
                        key={tab.name}
                        Icon={tab.icon}
                        dataFor={tab.name}
                        count={tab.count}
                      />
                    </div>
                    <div className='text-xs -mt-6'>{tab?.shortTitle ?? tab.title}</div>
                  </>
                )
              }
            })}
          </div>

          <ul className='flex flex-col items-center gap-5 mb-3'>
            <li>
              <div
                className='tooltip tooltip-left'
                data-tip='Go to Workviews'
              >
                <Link
                  href='/work-views'
                  data-test='side-panel-navigate-to-work-views'
                >
                  <RectangleStackIcon
                    className='cursor-pointer text-gray-400 hover:text-sky-50 hover:bg-sky-700 rounded-full p-2'
                    width={42}
                    height={42}
                  />
                </Link>
              </div>
            </li>
            <li>
              <div
                className='tooltip tooltip-left'
                data-tip='Logout'
              >
                <Link
                  href='/logout'
                  data-test='side-panel-logout-btn'
                >
                  <ArrowRightOnRectangleIcon
                    className='cursor-pointer text-gray-400 hover:text-red-100 hover:bg-red-700 rounded-full p-2'
                    width={42}
                    height={42}
                  />
                </Link>
              </div>
            </li>
            <li>
              {/* Feature Flag Switch */}
              <div
                className='tooltip tooltip-left'
                data-tip='Feature Flag Switch'
                data-for='feature-flag-switch'
                data-test='side-panel-feature-flag-btn'
              >
                <Switch
                  id='feature-flag-switch'
                  height={20}
                  width={40}
                  onColor='#ccc'
                  onHandleColor='#16a34a'
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onChange={() => setUserControlledFF()}
                  checked={isUserControlledFF ? isUserControlledFF : false}
                />
              </div>
            </li>
          </ul>
        </div>
        {open && (
          <div
            className={`w-[400px] ${
              activeSubTab === 'notes' ? 'pt-0 pb-4' : 'py-4'
            } border-l overflow-y-auto relative`}
            id='sidePanelScrollView'
          >
            <div
              className={`flex gap-3 justify-between items-center p-3 ${
                activeSubTab === 'notes' ? 'pt-[1rem] sticky top-0 z-50 bg-white' : ''
              }`}
            >
              <h2 className='font-bold text-lg flex-grow'>{activeTabObject?.title}</h2>
              <XMarkIcon
                className='cursor-pointer'
                width={20}
                onClick={() => {
                  dispatch(setDealAdvisorOpen(false))
                  patchUserPreferences({
                    id: user?.id,
                    ui_preferences: {
                      dealAdvisorOpen: false,
                      dealAdvisorTab: activeSubTab,
                    },
                  })
                }}
              />
            </div>
            {activeTabObject?.component}
          </div>
        )}
      </div>
    </div>
  )
}
